/**
 * TanStack Query hooks for CD Versioning
 */

import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { closingDisclosureApi } from '../services/closingDisclosureApi';
import type { CDVersion, CreateVersionRequest, RestoreVersionRequest, VersionDiff } from '../types';
import { cdQueryKeys } from './useClosingDisclosureQueries';

/**
 * Extended query keys for versioning
 */
export const versionQueryKeys = {
  ...cdQueryKeys,
  versions: (transactionId: string) =>
    [...cdQueryKeys.transaction(transactionId), 'versions'] as const,
  version: (transactionId: string, versionId: string) =>
    [...versionQueryKeys.versions(transactionId), versionId] as const,
  latestVersion: (transactionId: string) =>
    [...versionQueryKeys.versions(transactionId), 'latest'] as const,
  compareVersions: (transactionId: string, v1: string, v2: string) =>
    [...versionQueryKeys.versions(transactionId), 'compare', v1, v2] as const,
};

// ============================================================================
// Version Queries
// ============================================================================

/**
 * Transform snake_case API response to camelCase CDVersion
 */
function transformVersion(apiVersion: any): CDVersion {
  return {
    id: apiVersion.id,
    transactionId: apiVersion.transaction_id,
    versionNumber: apiVersion.version_number,
    versionType: apiVersion.version_type,
    snapshotData: apiVersion.snapshot_data,
    parentVersionId: apiVersion.parent_version_id,
    parentVersion: apiVersion.parentVersion,
    sourceDocumentId: apiVersion.source_document_id,
    sourceDocument: apiVersion.sourceDocument
      ? {
          id: apiVersion.sourceDocument.id,
          fileName: apiVersion.sourceDocument.file_name,
        }
      : undefined,
    notes: apiVersion.notes,
    createdByUserId: apiVersion.created_by_user_id,
    createdBy: apiVersion.createdBy,
    createdAt: apiVersion.createdAt || apiVersion.created_at,
  };
}

/**
 * Fetch all versions for a transaction
 */
export function useVersions(
  transactionId: string,
  options?: Omit<UseQueryOptions<CDVersion[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: versionQueryKeys.versions(transactionId),
    queryFn: async () => {
      const response = await closingDisclosureApi.getVersions(transactionId);
      // Transform snake_case API response to camelCase
      return (response.data.versions || []).map(transformVersion);
    },
    enabled: !!transactionId,
    ...options,
  });
}

/**
 * Fetch the latest version for a transaction
 */
export function useLatestVersion(
  transactionId: string,
  options?: Omit<UseQueryOptions<CDVersion | null, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: versionQueryKeys.latestVersion(transactionId),
    queryFn: async () => {
      const response = await closingDisclosureApi.getVersions(transactionId);
      const versions = response.data.versions;
      if (!versions || versions.length === 0) return null;
      // Return the version with the highest version_number
      return versions.reduce((latest, current) =>
        current.version_number > latest.version_number ? current : latest,
      ) as unknown as CDVersion;
    },
    enabled: !!transactionId,
    ...options,
  });
}

/**
 * Fetch a specific version
 */
export function useVersion(
  transactionId: string,
  versionId: string,
  options?: Omit<UseQueryOptions<CDVersion, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: versionQueryKeys.version(transactionId, versionId),
    queryFn: async () => {
      const response = await closingDisclosureApi.getVersions(transactionId);
      const version = response.data.versions.find((v) => v.id === versionId);
      if (!version) throw new Error('Version not found');
      return version as unknown as CDVersion;
    },
    enabled: !!transactionId && !!versionId,
    ...options,
  });
}

/**
 * Compare two versions (returns diff from API)
 */
export function useCompareVersions(
  transactionId: string,
  versionId1: string,
  _versionId2: string,
  options?: Omit<UseQueryOptions<VersionDiff, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: versionQueryKeys.compareVersions(transactionId, versionId1, _versionId2),
    queryFn: async () => {
      const response = await closingDisclosureApi.getVersionDiff(transactionId, versionId1);
      return response.data as unknown as VersionDiff;
    },
    enabled: !!transactionId && !!versionId1 && !!_versionId2,
    ...options,
  });
}

// ============================================================================
// Version Mutations
// ============================================================================

/**
 * Create a new version
 */
export function useCreateVersion(
  options?: Omit<
    UseMutationOptions<
      { version: CDVersion; message: string },
      Error,
      { transactionId: string; request?: CreateVersionRequest },
      unknown
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, request }) => {
      const response = await closingDisclosureApi.createVersion(transactionId, {
        versionType: request?.versionType || 'draft',
        notes: request?.notes,
      });
      return { version: response.data as unknown as CDVersion, message: 'Version created' };
    },
    ...options,
    onSuccess: (_data, variables) => {
      // Invalidate versions list and latest version
      queryClient.invalidateQueries({
        queryKey: versionQueryKeys.versions(variables.transactionId),
      });
      queryClient.invalidateQueries({
        queryKey: versionQueryKeys.latestVersion(variables.transactionId),
      });
      // Also invalidate transaction to update current_version_id
      queryClient.invalidateQueries({
        queryKey: cdQueryKeys.transaction(variables.transactionId),
      });
    },
  });
}

/**
 * Restore a previous version
 */
export function useRestoreVersion(
  options?: Omit<
    UseMutationOptions<
      { version: CDVersion; message: string },
      Error,
      { transactionId: string; versionId: string; request?: RestoreVersionRequest },
      unknown
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, versionId, request: _request }) => {
      const response = await closingDisclosureApi.restoreVersion(transactionId, versionId);
      return { version: response.data as unknown as CDVersion, message: 'Version restored' };
    },
    ...options,
    onSuccess: (_data, variables) => {
      // Invalidate all related queries after restore
      queryClient.invalidateQueries({
        queryKey: cdQueryKeys.transaction(variables.transactionId),
      });
      queryClient.invalidateQueries({
        queryKey: versionQueryKeys.versions(variables.transactionId),
      });
      queryClient.invalidateQueries({
        queryKey: cdQueryKeys.loanTerms(variables.transactionId),
      });
      queryClient.invalidateQueries({
        queryKey: cdQueryKeys.parties(variables.transactionId),
      });
      queryClient.invalidateQueries({
        queryKey: cdQueryKeys.lineItems(variables.transactionId),
      });
      queryClient.invalidateQueries({
        queryKey: cdQueryKeys.calculations(variables.transactionId),
      });
    },
  });
}
